import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import {
  useAdminGetSessionAdmissionCardsQuery,
  useAdminGetExamSessionQuery,
} from "../../api/examsApi";

const niqsLogo = "/niqs-logo.png";

function PhotoCell({ url }: { url?: string | null }) {
  const [src, setSrc] = useState<string | null>(url ?? null);

  useEffect(() => {
    if (!url) { setSrc(null); return; }
    setSrc(url);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d")!.drawImage(img, 0, 0);
        setSrc(canvas.toDataURL("image/jpeg"));
      } catch {
        // CORS blocked canvas read — keep direct URL
      }
    };
    img.onerror = () => {};
    img.src = url;
  }, [url]);

  return (
    <div
      className="border border-slate-400 overflow-hidden bg-slate-100 flex items-center justify-center flex-shrink-0"
      style={{ width: 56, height: 68 }}
    >
      {src ? (
        <img src={src} alt="" className="w-full h-full object-cover" />
      ) : (
        <span className="text-[6px] text-slate-300 text-center leading-tight px-0.5">
          NO PHOTO
        </span>
      )}
    </div>
  );
}

export default function AdminSessionAttendanceListPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const sid = Number(sessionId);

  const { data: sessionData } = useAdminGetExamSessionQuery(sid);
  const { data, isLoading, isError } = useAdminGetSessionAdmissionCardsQuery(sid);

  const session = sessionData?.session ?? sessionData;
  const cards: any[] = data?.cards ?? [];

  const examStages: string[] = Array.from(
    new Set(cards.map((c) => c.applicant?.exam_stage).filter(Boolean))
  ).sort() as string[];

  const centerNames: string[] = Array.from(
    new Set(cards.map((c) => c.applicant?.center).filter(Boolean))
  ).sort() as string[];

  const [selectedExam, setSelectedExam] = useState("__all__");
  const [selectedCenter, setSelectedCenter] = useState("__all__");
  const [selectedCourse, setSelectedCourse] = useState("__all__");

  // Courses from cards matching the current exam + center selection
  const examCourses: { id: string | number; code: string; name: string }[] = (() => {
    if (selectedExam === "__all__") return [];
    const seen = new Map<string, { id: string | number; code: string; name: string }>();
    for (const card of cards) {
      if (card.applicant?.exam_stage !== selectedExam) continue;
      if (selectedCenter !== "__all__" && card.applicant?.center !== selectedCenter) continue;
      for (const course of card.applicant?.courses ?? []) {
        if (course?.id && !seen.has(String(course.id))) {
          seen.set(String(course.id), course);
        }
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.code.localeCompare(b.code));
  })();

  const filtered = cards.filter((c) => {
    const examMatch = selectedExam === "__all__" || c.applicant?.exam_stage === selectedExam;
    const centerMatch = selectedCenter === "__all__" || c.applicant?.center === selectedCenter;
    const courseMatch =
      selectedCourse === "__all__" ||
      (c.applicant?.courses ?? []).some((cr: any) => String(cr?.id) === selectedCourse);
    return examMatch && centerMatch && courseMatch;
  });

  // Build: exam → center → sorted candidates
  const groupedByExam: Record<string, Record<string, any[]>> = {};
  for (const card of filtered) {
    const exam = card.applicant?.exam_stage ?? "Unknown";
    const center = card.applicant?.center ?? "No Center Assigned";
    if (!groupedByExam[exam]) groupedByExam[exam] = {};
    if (!groupedByExam[exam][center]) groupedByExam[exam][center] = [];
    groupedByExam[exam][center].push(card);
  }
  for (const exam of Object.keys(groupedByExam)) {
    for (const center of Object.keys(groupedByExam[exam])) {
      groupedByExam[exam][center].sort((a, b) =>
        (a.applicant?.exam_no ?? "").localeCompare(b.applicant?.exam_no ?? "")
      );
    }
  }

  // Flatten: one entry per exam+center = one printed page
  const examNames = Object.keys(groupedByExam).sort();
  type Section = { examName: string; centerName: string; apps: any[]; isFirstForExam: boolean };
  const sections: Section[] = [];
  for (const examName of examNames) {
    Object.keys(groupedByExam[examName])
      .sort()
      .forEach((centerName, ci) => {
        sections.push({
          examName,
          centerName,
          apps: groupedByExam[examName][centerName],
          isFirstForExam: ci === 0,
        });
      });
  }

  const total = filtered.length;

  return (
    <>
      <style>{`
        /* Hide print-only elements on screen */
        .print-only { display: none; }

        @media print {
          /* ── Hide portal chrome ── */
          header, aside { display: none !important; }

          /* ── Fix flex layout height constraint (aside forces 100vh height) ── */
          html, body {
            height: auto !important;
            overflow: visible !important;
          }
          .flex {
            display: block !important;
            height: auto !important;
            overflow: visible !important;
          }
          .min-h-screen { min-height: 0 !important; }
          main {
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          /* breadcrumb div that lives in main before <Outlet /> */
          main > div:first-child { display: none !important; }

          /* ── Toggle visibility ── */
          .no-print  { display: none !important; }
          .print-only { display: block !important; }

          /* ── Colour fidelity ── */
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

          /* ── Page layout ── */
          body { font-size: 11px; margin: 0; padding: 0; }
          table { page-break-inside: auto; }
          tr    { page-break-inside: avoid; }

          /* Each exam+center section = one fresh page */
          .print-section + .print-section { page-break-before: always; }
        }
      `}</style>

      <div className="p-6 bg-white min-h-screen">
        {/* ── Toolbar (screen only) ── */}
        <div className="no-print flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to={`/exams/admin/sessions/${sid}`}
              className="p-2 rounded-xl hover:bg-slate-100 transition-all"
            >
              <ArrowLeft className="w-4 h-4 text-slate-600" />
            </Link>
            <div>
              <h1 className="text-lg font-black text-slate-900 uppercase tracking-widest">
                Attendance List
              </h1>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">
                {session?.title ?? "Loading…"} &nbsp;·&nbsp; {total} candidate
                {total !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedExam}
              onChange={(e) => { setSelectedExam(e.target.value); setSelectedCourse("__all__"); }}
              className="border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50 outline-none"
            >
              <option value="__all__">All Exams</option>
              {examStages.map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
            <select
              value={selectedCenter}
              onChange={(e) => { setSelectedCenter(e.target.value); setSelectedCourse("__all__"); }}
              className="border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50 outline-none"
            >
              <option value="__all__">All Centers</option>
              {centerNames.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              disabled={selectedExam === "__all__" || examCourses.length === 0}
              className="border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50 outline-none disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <option value="__all__">
                {selectedExam === "__all__"
                  ? "Select an exam first"
                  : examCourses.length === 0
                  ? "No courses registered"
                  : "All Courses"}
              </option>
              {examCourses.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.code} — {c.name}</option>
              ))}
            </select>
            <button
              onClick={() => window.print()}
              disabled={isLoading || total === 0}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 disabled:opacity-50 transition-all"
            >
              <Printer className="w-4 h-4" />
              Print List
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
          </div>
        )}
        {isError && (
          <p className="text-center text-rose-600 font-bold py-20">
            Could not load attendance data.
          </p>
        )}
        {!isLoading && total === 0 && (
          <div className="text-center py-20">
            <p className="text-slate-400 font-black uppercase tracking-widest text-sm">
              No candidates found.
            </p>
          </div>
        )}

        {/* ── One section per exam + center ── */}
        {sections.map(({ examName, centerName, apps, isFirstForExam }, idx) => (
          <div key={`${examName}-${centerName}`} className={`print-section ${idx > 0 ? "mt-16" : ""}`}>

            {/* Screen: exam group header (orange accent, first center of each exam only) */}
            {isFirstForExam && (
              <div className="no-print flex items-center gap-3 bg-slate-900 text-white px-5 py-3 mb-4 rounded-xl">
                <div className="w-1 h-6 bg-orange-500 rounded-full flex-shrink-0" />
                <span className="font-black text-sm uppercase tracking-widest text-orange-400">
                  {examName}
                </span>
                <span className="font-bold text-xs text-slate-400 ml-auto">
                  {Object.values(groupedByExam[examName]).flat().length} candidate
                  {Object.values(groupedByExam[examName]).flat().length !== 1 ? "s" : ""}
                </span>
              </div>
            )}

            {/* ── Print-only page header (hidden on screen via .print-only class) ── */}
            <div className="print-only">
              {/* NIQS masthead */}
              <div className="flex items-center gap-4 pb-3 mb-3 border-b-2 border-slate-900">
                <img
                  src={niqsLogo}
                  alt="NIQS"
                  className="h-16 w-16 object-contain flex-shrink-0"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
                <div className="flex-1 text-center">
                  <h1 className="text-base font-black uppercase tracking-widest leading-tight">
                    THE NIGERIAN INSTITUTE OF QUANTITY SURVEYORS
                  </h1>
                  <p className="text-xs font-bold uppercase text-slate-600 mt-0.5">
                    {session?.year ?? ""} Professional Examinations
                  </p>
                  <p className="text-xs font-black uppercase tracking-widest text-slate-900 mt-0.5">
                    Examination Attendance List
                  </p>
                </div>
              </div>

              {/* Metadata strip: exam name, centre, date */}
              <div
                className="grid grid-cols-2 gap-x-8 gap-y-3 mb-4 px-3 py-3"
                style={{ border: "1.5px solid #0f172a" }}
              >
                <div className="flex items-end gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 whitespace-nowrap">
                    Examination:
                  </span>
                  <span
                    className="flex-1 font-black text-[11px] uppercase tracking-wider text-slate-900 pb-0.5"
                    style={{ borderBottom: "1px solid #0f172a" }}
                  >
                    {examName}
                  </span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 whitespace-nowrap">
                    Date:
                  </span>
                  <span
                    className="flex-1 pb-0.5"
                    style={{ borderBottom: "1px solid #0f172a", minWidth: 120, display: "inline-block" }}
                  >
                    &nbsp;
                  </span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 whitespace-nowrap">
                    Centre:
                  </span>
                  <span
                    className="flex-1 font-bold text-[11px] uppercase text-slate-900 pb-0.5"
                    style={{ borderBottom: "1px solid #0f172a" }}
                  >
                    {centerName}
                  </span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 whitespace-nowrap">
                    No. of Candidates:
                  </span>
                  <span
                    className="flex-1 font-black text-[11px] text-slate-900 pb-0.5"
                    style={{ borderBottom: "1px solid #0f172a" }}
                  >
                    {apps.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Centre sub-header — screen + print */}
            <div className="flex items-center justify-between bg-slate-900 text-white px-4 py-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-0.5 h-4 bg-orange-500" />
                <span className="font-black text-xs uppercase tracking-widest">
                  {centerName}
                </span>
              </div>
              <span className="font-bold text-xs text-slate-300">
                {apps.length} candidate{apps.length !== 1 ? "s" : ""}
              </span>
            </div>

            {/* Candidate table */}
            <table className="w-full border-collapse text-sm mb-6">
              <thead>
                <tr className="border-b-2 border-slate-900">
                  <th className="text-left py-2 px-2 font-black text-[10px] uppercase tracking-wider text-slate-600 w-8">#</th>
                  <th className="text-left py-2 px-2 font-black text-[10px] uppercase tracking-wider text-slate-600 w-16">Photo</th>
                  <th className="text-left py-2 px-2 font-black text-[10px] uppercase tracking-wider text-slate-600">Exam No</th>
                  <th className="text-left py-2 px-2 font-black text-[10px] uppercase tracking-wider text-slate-600">Candidate Name</th>
                  <th className="text-left py-2 px-2 font-black text-[10px] uppercase tracking-wider text-slate-600">Membership No</th>
                  <th className="py-2 px-2 font-black text-[10px] uppercase tracking-wider text-slate-600 text-center w-32">Signature</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((card, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                    <td className="py-1.5 px-2 text-slate-400 font-bold text-xs align-middle">{i + 1}</td>
                    <td className="py-1.5 px-2 align-middle">
                      <PhotoCell url={card.applicant?.photo_url} />
                    </td>
                    <td className="py-1.5 px-2 font-black text-xs text-slate-900 uppercase align-middle">
                      {card.applicant?.exam_no ?? "—"}
                    </td>
                    <td className="py-1.5 px-2 font-bold text-xs text-slate-800 uppercase align-middle">
                      {card.applicant?.name ?? "—"}
                    </td>
                    <td className="py-1.5 px-2 font-bold text-xs text-slate-600 align-middle">
                      {card.applicant?.membership_no ?? "—"}
                    </td>
                    <td className="py-1.5 px-2 border-b border-slate-300 w-32 align-middle" />
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Print-only footer: invigilator sign-off */}
            <div className="print-only flex justify-between pt-3 border-t border-slate-300 mt-2">
              <div className="flex items-end gap-2">
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                  Invigilator's Signature:
                </span>
                <span style={{ borderBottom: "1px solid #0f172a", minWidth: 160, display: "inline-block" }}>&nbsp;</span>
              </div>
              <div className="flex items-end gap-2">
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                  Date:
                </span>
                <span style={{ borderBottom: "1px solid #0f172a", minWidth: 120, display: "inline-block" }}>&nbsp;</span>
              </div>
            </div>

          </div>
        ))}

        {/* Grand total (screen only) */}
        {total > 0 && (
          <div className="no-print mt-6 pt-4 border-t-2 border-slate-900 text-[10px] text-slate-500 font-bold uppercase tracking-widest flex justify-between">
            <span>Grand Total: {total} candidate{total !== 1 ? "s" : ""}</span>
            <span>
              Generated:{" "}
              {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
